import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Users, Clock, ClipboardList, Receipt, MessageSquare, FileSignature, LayoutGrid } from "lucide-react";
import { setPresenceSection } from "@/lib/presence";
import { PlatformTab } from "@/components/vendor/PlatformTab";
import { TeaLogo } from "@/components/TeaLogo";
import { SignList } from "@/components/docs/SignList";
import { vendorDocsApi } from "@/lib/docsApi";
import { VENDOR_IMP_KEY, cvApi, signOutVendor, vendorToken } from "@/lib/vendorApi";
import { LoginAsBar, exitLoginAs } from "@/components/console/PreviewAs";
import { VendorOnboard } from "@/components/vendor/VendorOnboard";
import { WorkerDetail, WorkerList } from "@/components/vendor/Workers";
import { HoursTab, InvoicesTab } from "@/components/vendor/HoursInvoices";
import { TimesheetsTab } from "@/components/vendor/Timesheets";
import { MessagesTab } from "@/components/vendor/Messages";

const TABS = [["timesheets", "Submit Hours", ClipboardList], ["workers", "My workers", Users], ["hours", "Hours", Clock], ["invoices", "Invoices", Receipt], ["messages", "Memos", MessageSquare], ["docs", "Agreements", FileSignature], ["platform", "Platform", LayoutGrid]];
const NEEDS_WORKERS = ["timesheets", "workers", "hours", "invoices", "messages"];

const Body = ({ me, reload }) => {
  const tabs = TABS.filter(([k]) => (k === "platform" ? (me.platform_cards || []).length > 0 : (me.portal_cards || []).includes(k)));
  const [tab, setTab] = useState(tabs[0]?.[0] || "");
  const [open, setOpen] = useState(null);
  const [to, setTo] = useState("");
  const [workers, setWorkers] = useState([]);
  const onCount = useCallback((n) => { if (n === 0) reload(); }, [reload]);
  const loadWorkers = me.ready && (me.portal_cards || []).some((k) => NEEDS_WORKERS.includes(k));
  useEffect(() => { if (loadWorkers) cvApi.get("/residents").then((d) => setWorkers(d.residents)).catch(() => {}); }, [loadWorkers]);
  useEffect(() => { setPresenceSection(!me.onboarded ? "Business details" : !me.ready ? "Signing agreements" : (TABS.find(([k]) => k === tab) || [])[1]); }, [tab, me.onboarded, me.ready]);
  if (!me.onboarded) return <VendorOnboard me={me} onDone={reload} />;
  if (!me.ready) return (
    <div className="space-y-4" data-testid="vendor-docs-gate">
      <div className="rounded-lg border border-amber-400/40 bg-amber-500/10 p-4 text-sm text-amber-100">Please sign your Contract Vendor agreements first ({me.docs_waiting} waiting). Your workers appear here as soon as they're done.</div>
      <SignList api={vendorDocsApi} base="/vendor" prefix="vendor-docs" onCount={onCount} />
      <button type="button" onClick={reload} data-testid="vendor-docs-recheck" className="text-sm text-[#d4a017]">I've signed them — continue</button>
    </div>
  );
  const message = (id) => { setTo(id); setTab("messages"); setOpen(null); };
  return (
    <div className="space-y-6">
      <nav className="flex flex-wrap gap-2" data-testid="vendor-tabs">
        {tabs.map(([k, l, Icon]) => (
          <button key={k} type="button" onClick={() => { setTab(k); setOpen(null); }} data-testid={`vendor-tab-${k}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border transition-colors ${tab === k ? "bg-[#d4a017] text-black border-[#d4a017]" : "border-white/15 text-white/70 hover:text-white"}`}><Icon className="h-4 w-4" /> {l}</button>))}
      </nav>
      {tab === "workers" && (open ? <WorkerDetail id={open} onBack={() => setOpen(null)} onMessage={message} /> : <WorkerList rows={workers} onOpen={setOpen} />)}
      {tab === "timesheets" && <TimesheetsTab workers={workers} />}
      {tab === "hours" && <HoursTab workers={workers} />}
      {tab === "invoices" && <InvoicesTab workers={workers} />}
      {tab === "messages" && <MessagesTab workers={workers} to={to} />}
      {tab === "docs" && <SignList api={vendorDocsApi} base="/vendor" prefix="vendor-docs" />}
      {tab === "platform" && <PlatformTab cards={me.platform_cards} />}
      {!tabs.length && <div className="text-sm text-white/60" data-testid="vendor-no-cards">No cards are turned on for your account yet. Contact TEA staff.</div>}
    </div>
  );
};

export default function VendorPortalPage() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const load = useCallback(() => cvApi.get("/me").then(setMe).catch(() => { signOutVendor(); nav("/vendor/login"); }), [nav]);
  useEffect(() => { if (!vendorToken()) nav("/vendor/login"); else load(); }, [load, nav]);
  const exitAs = async () => { await exitLoginAs(sessionStorage.getItem(VENDOR_IMP_KEY)); sessionStorage.removeItem(VENDOR_IMP_KEY); window.close(); setTimeout(() => { window.location.href = "/admin"; }, 150); };
  if (!me) return <div className="min-h-screen bg-[#07080a]" />;
  return (
    <div className="min-h-screen bg-[#07080a] text-white" data-testid="vendor-portal">
      {me.login_as_by && sessionStorage.getItem(VENDOR_IMP_KEY) && <LoginAsBar name={me.vendor.name || me.vendor.company} onExit={exitAs} />}
      <header className="border-b border-white/10 px-5 sm:px-10 py-4 flex items-center gap-3">
        <TeaLogo size={36} testid="vendor-portal-logo" />
        <div className="flex-1 min-w-0"><div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#d4a017]">Contract Vendor Portal</div>
          <div className="font-semibold truncate" data-testid="vendor-portal-name">{me.vendor.company || me.vendor.name}</div></div>
        <button type="button" onClick={() => { if (me.login_as_by) { exitAs(); return; } signOutVendor(); nav("/vendor/login"); }} data-testid="vendor-signout" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white"><LogOut className="h-4 w-4" /> Sign out</button>
      </header>
      <main className="max-w-4xl mx-auto px-5 sm:px-10 py-8"><Body me={me} reload={load} /></main>
    </div>
  );
}
